import { useEffect, useState } from 'react';
import { Button, Empty, ErrorNote, Panel, relativeTime } from './ui';

/**
 * Cross-tenant health, for the operator.
 *
 * The failure this exists to catch is a shared integration breaking for everyone at once —
 * a weather provider changing its response shape, say. Seen one installation at a time
 * that looks like several unrelated faults; seen together it is obviously one.
 */

interface FailingSource {
  id: string;
  slug: string;
  name: string;
  tenant: string;
  consecutive_failures: number;
  last_error: string | null;
  last_run_at: string | null;
  last_success_at: string | null;
}

export function OperatorPanel({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<FailingSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (): void => {
    void fetch('/api/operator/failing', { credentials: 'include' })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error ?? 'failed to load');
        setRows(data.failing as FailingSource[]);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, []);

  // Sources failing with the same message across installations are almost certainly one
  // upstream problem, so group them rather than listing the same error many times.
  const grouped = new Map<string, FailingSource[]>();
  for (const r of rows ?? []) {
    const key = r.last_error ?? 'unknown error';
    grouped.set(key, [...(grouped.get(key) ?? []), r]);
  }

  return (
    <Panel
      title="Failing sources, all installations"
      actions={
        <>
          <Button onClick={load}>Refresh</Button>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {error && <ErrorNote>{error}</ErrorNote>}
      {rows === null ? (
        <Empty>Loading…</Empty>
      ) : rows.length === 0 ? (
        <Empty>Nothing is failing right now.</Empty>
      ) : (
        <div className="space-y-4">
          {[...grouped.entries()].map(([message, sources]) => (
            <div key={message}>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="mono text-xs text-bad truncate">{message}</span>
                {sources.length > 1 && (
                  <span className="text-xs text-warn shrink-0">
                    {sources.length} sources across {new Set(sources.map((s) => s.tenant)).size} installation(s) —
                    likely one upstream problem
                  </span>
                )}
              </div>
              <div className="border border-ink-800 rounded-md divide-y divide-ink-800">
                {sources.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                    <span className="text-ink-500 w-32 truncate">{s.tenant}</span>
                    <span className="text-ink-100 flex-1 truncate">{s.name}</span>
                    <span className="mono text-ink-500 w-24 truncate">{s.slug}</span>
                    <span className="text-bad w-16 text-right">×{s.consecutive_failures}</span>
                    <span className="text-ink-500 w-28 text-right" title="last successful poll">
                      ok {relativeTime(s.last_success_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
